import {createSessionAndRetrieveTokensV2} from '@actions/cookie';
import {transformAndReorderForm, getCurrentOrigin} from '@actions/formManipulation';
import {
  filterTraining,
  getConsentPhrases,
  getTraining,
  retrieveAndSetUTM,
  getFormValues
} from '@actions/formulary';
import {useMultiStepContext} from '@components/hoc/MultiStepForm/MultiStepFormContext';
import {defaultConsentPhrasesIds} from '@config/forms';
import React, {useState, useEffect} from 'react';
import {formConfig} from '@config/formConfig';
import { domainsForTrainings, overridesTrainingNames } from '@config/lists';
import { useGTMDispatch } from '@components/hoc/GTM/GTM';
import { v4 as uuidv4 } from 'uuid';
import { getMetaEventIdFromStorage } from '@actions/formulary';

const MultiStepInitializer = ({setInitialized, setApiError, handleFormData, setMetaEventId }) => {
  const {
    trainingId,
    trainingName,
    config,
    formDataContext,
    setTrainingName,
    setTrainingId,
    setTrainingInfos,
    setConfig,
    setPreloadedValues,
    setCookiesUTM,
    resetFormData,
    updateFormData
  } = useMultiStepContext();
  const sendDataToGTM = useGTMDispatch();
  const introPage = !!global.window?.Config.introPage ?? !!formConfig.introPage;

  //! AJOUT DE QUERYPARAMS POUR CHARGEMENT DU FORMULAIRE

  const [formConfigState, setFormConfigState] = useState({});

  useEffect(() => {

    let preLoadedValues = getFormValues();
    preLoadedValues = preLoadedValues.filter(item => item.key !== "niveauEtudes");
    setPreloadedValues(preLoadedValues);

    const initializerFunc = async () => {

      const formConfigFile = formConfig.form;
      const searchParams = new URLSearchParams(window.location.search);
      const trainingIdConfig = preLoadedValues.find(elem => elem?.key === "training")?.value;
      const domainIdConfig = preLoadedValues.find(elem => elem?.key === "domain")?.value;
      const startingStep = preLoadedValues.find(elem => elem?.key === "etape")?.value;

      const pathName = window.location.pathname;

      try {
        const loopObject = {};

        if (!formConfigState.origin) {
          const origin = getCurrentOrigin() + pathName;
          sendDataToGTM({ event: 'setOrigin', origin: origin });
          loopObject.origin = origin;
        }

        //! Création du Meta Event Id avant de passer en initalisé (on utilise uuid v4 pour générer un id unique)
        // creation uniquement sur l'intro page ou si ça n'existe pas en storage
        const metaEventIdGenerator = uuidv4();
        //reset metaEventId on intro page
        //recuperer le metaId s'il existe en localStorage
        const previousMetaEventId = introPage ? null : getMetaEventIdFromStorage();
        //pour pouvoir identifier l'utilisateur qui passe de l'intro Page à la multipage
        let finalMetaEventId = previousMetaEventId ?? metaEventIdGenerator;

        setMetaEventId(finalMetaEventId);

        //save metaEventId to context and localstorage
        updateFormData({ name: "metaEventId", value: finalMetaEventId });
        sendDataToGTM({event: 'setMetaEventId', metaEventId: finalMetaEventId });

        // Recupération des UTM et les ajouter dans les Cookies pour les utiliser au futur
        retrieveAndSetUTM(handleFormData);
        setCookiesUTM(createSessionAndRetrieveTokensV2());

        if (searchParams.get('utm_source') === 'manuel') {
          resetFormData();
        }

        if (!formConfigState.phrases) {
          //parfois le consent peut être vide
          const consentPhrases = getConsentPhrases();
          if (consentPhrases) {

            let selectedPhrases = [];
            Object.values(consentPhrases).forEach((phrase) => {
              Object.keys(defaultConsentPhrasesIds).forEach((key) => {
                if (defaultConsentPhrasesIds[key] === phrase.id) {
                  selectedPhrases[phrase.id] = { ...phrase, key };
                }
              });
            });
            selectedPhrases = selectedPhrases.filter(
              (item) => item !== undefined
            );
            loopObject.phrases = selectedPhrases;
          } else {
            console.error("erreur pendant fetch des consent , reloader la page")
          }
        }

        let newFormConfig = transformAndReorderForm(
          startingStep - 1,
          formConfigFile
        );

        // Configuration de l'affichage du formulaire avec le nombre d'étapes en fonction de comment fonctionne le formulaire (intro, saut d'étapes etc)

        //correspond toujours à ce qui configuré dans config/configForm
        loopObject.config = formConfigFile;
        loopObject.visibleSteps = newFormConfig;
        //return only element that do not have a preloaded value
        loopObject.visibleSteps = loopObject.visibleSteps.filter(
          (item) => {
            return preLoadedValues.find(elem => elem?.key === item.key) === undefined;
          }
        );

        // Initialiseur pour thématique spécifique, sinon fallback à celle en localStorage, ou encore retour à null
        loopObject.domainId =
          (Number(domainIdConfig) === 0
            ? formDataContext?.domainId
            : domainIdConfig) ?? null;

        //Initialiseur pour formation spécifique
        if (
          (!formConfigState.trainingId || !formConfigState.trainingName) &&
          trainingIdConfig &&
          trainingIdConfig !== 0
        ) {
          const data = await getTraining(trainingIdConfig);

          const trainingKeyName = Object.keys(data)[0];
          const training = data[trainingKeyName];
          console.log("training data", training)
          training.id = trainingName;
          training.type = filterTraining(training);
          training.trainingNameId = Array.isArray(overridesTrainingNames[training?.ID_FORMATION_BITRIX]) ? 1 : 0;
          setTrainingName(Object.keys(data)[0]);
          setTrainingId(training?.ID_FORMATION_BITRIX);
          setTrainingInfos(training);
          loopObject.trainingId = training?.ID_FORMATION_BITRIX;
          loopObject.trainingName = Object.keys(data)[0];
          sendDataToGTM({
            event: 'setThematique',
            idFormationBitrix: loopObject.trainingId,
            formation: trainingKeyName,
          });

          if (
            !loopObject.domainId ||
            !domainsForTrainings[training?.ID_FORMATION_BITRIX]?.domains.includes(
              loopObject.domainId,
            )
          ) {
            // Dans le cas où il n'y aurait pas de domainId ou encore que le domainId ne corresponde pas à la formation
            // alors on change cela en mettant la première thématique de la formation par défaut au lieu d'en mettre une erronée.
            loopObject.domainId = domainsForTrainings[training?.ID_FORMATION_BITRIX]?.domains[0];
          }
        }
        loopObject.maxStep = loopObject.visibleSteps.length;

        setFormConfigState(loopObject);
      } catch (error) {
        console.log('error', error);
      }
    };

    initializerFunc();
    // Retrait de l'array de dépendances parce que dans certains cas
    // (ceux où il n'y a pas de trainingId défini par exemple)
  }, []);

  //on s'assure que le context a été initialisé avec l'initializer et non seulement avec les valeurs du localstorage avant de supposer
  //que l'initialization est terminée
  useEffect(() => {

    if (
      formConfigState?.origin &&
      formConfigState?.config &&
      formConfigState?.maxStep &&
      formConfigState?.phrases
    ) {
      //nouvelle config est enregistrée dans le context
      setConfig(formConfigState);
      setInitialized(true);
    }
  }, [formConfigState]);

  return <></>;
};

export default MultiStepInitializer;
